import type { MouseEvent } from 'react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { styled } from 'baseui';
import { Spinner } from 'baseui/spinner';
import { type ReferenceMouseEvent } from '../../../image/editor-canvas.react';
import { getImageDimensions } from './utils';
export type CanvasImageRendererProps = {
  src: string;
  scale: number;
  rotation: number;
  offsets: {
    x: number;
    y: number;
  };
  expanded: boolean;
  enableImageSelection?: boolean;
  onReferenceMouseUp?: (e: ReferenceMouseEvent) => void;
  onReferenceMouseDown?: (e: ReferenceMouseEvent) => void;
  onReferenceMouseMove?: (e: ReferenceMouseEvent) => void;
  onMouseWheelMove?: (e: number) => void;
  onImageLoaded?: (imageData: string) => void;
  hideAspectRatio?: boolean;
};
const Container = styled('div', ({
  $theme
}) => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  overflow: 'hidden',
  background: $theme.colors.backgroundTertiary,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));
const ImageDetails = styled('div', ({
  $theme: {
    colors,
    sizing,
    typography
  }
}) => ({
  ...typography.ParagraphSmall,
  padding: sizing.scale100,
  position: 'absolute',
  bottom: 0,
  right: 0,
  background: colors.backgroundSecondary,
  opacity: 0.85
}));
const StyledSpinner = styled(Spinner, {
  position: 'absolute',
  top: '50%',
  left: '50%'
});

// @ts-expect-error todo(ts-migration) TS2554 Expected 2 arguments, but got 1.
const ErrorBlock = styled('h4');
const HiddenImage = styled('img', () => ({
  display: 'none'
}));
export function CanvasImageRenderer({
  src,
  scale,
  rotation,
  offsets,
  expanded,
  enableImageSelection,
  onReferenceMouseUp,
  onReferenceMouseDown,
  onReferenceMouseMove,
  onMouseWheelMove,
  onImageLoaded,
  hideAspectRatio = false
}: CanvasImageRendererProps = {
  src: '',
  scale: 1,
  rotation: 0,
  offsets: {
    x: 0,
    y: 0
  },
  expanded: false,
  enableImageSelection: false,
  onReferenceMouseDown: undefined,
  onReferenceMouseMove: undefined,
  onReferenceMouseUp: undefined,
  onMouseWheelMove: undefined,
  onImageLoaded: undefined
}) {
  const containerRef = useRef();
  const canvasRef = useRef();
  const imgRef = useRef();
  const [canvasSize, setCanvasSize] = useState({
    w: 0,
    h: 0
  });
  const [imageSize, setImageSize] = useState({
    w: 0,
    h: 0
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(0);
  const [isError, setIsError] = useState(false);
  function unScaleFn(n: number) {
    if (scale !== undefined) {
      return n / scale;
    }
    return n;
  }
  function tranformMouseEventForReference(e: MouseEvent<HTMLCanvasElement>) {
    const container = e.currentTarget.getBoundingClientRect();
    return {
      ...e,
      referenceX: unScaleFn(e.clientX - container.left) - offsets.x,
      referenceY: unScaleFn(e.clientY - container.top) - offsets.y,
      /* $FlowFixMe This comment suppresses an error found when enabling type
       * checking of the legacy common/shared/ packages in the monorepo. To
       * view the error, delete this comment and run Flow. */
      referenceMovementX: unScaleFn(e.movementX),
      /* $FlowFixMe This comment suppresses an error found when enabling type
       * checking of the legacy common/shared/ packages in the monorepo. To
       * view the error, delete this comment and run Flow. */
      referenceMovementY: unScaleFn(e.movementY)
    };
  }

  // show loading spinner until new image is loaded on src change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImageLoaded(false);
  }, [src]);
  const setSizes = () => {
    if (imgRef.current) {
      const img = imgRef.current;
      // @ts-expect-error todo(ts-migration) TS2339 Property 'width' does not exist on type 'never'.
      setImageSize({
        w: img.width,
        h: img.height
      });
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'string | 0' is not assignable to parameter of type 'SetStateAction<number>'.
      setAspectRatio(img.height > 0 ? (img.width / img.height).toFixed(2) : 0);
    }
    if (containerRef.current) {
      const container = containerRef.current;
      // @ts-expect-error todo(ts-migration) TS2339 Property 'getBoundingClientRect' does not exist on type 'never'.
      const {
        width: stageWidth,
        height: stageHeight
      } = container.getBoundingClientRect();
      setCanvasSize({
        w: stageWidth,
        h: stageHeight
      });
    }
  };
  useEffect(() => {
    window.addEventListener('resize', () => {
      setSizes();
    });
    return () => {
      window.removeEventListener('resize', () => {
        setSizes();
      });
    };
  }, []);
  useEffect(() => {
    setSizes();
  }, [imageLoaded, expanded]);
  useLayoutEffect(() => {
    if (imgRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      // @ts-expect-error todo(ts-migration) TS2339 Property 'getContext' does not exist on type 'never'.
      const canvasContext = canvas.getContext('2d');
      const {
        w: stageWidth,
        h: stageHeight
      } = canvasSize;
      const {
        w: domImageWidth,
        h: domImageHeight
      } = imageSize;
      if (!domImageWidth || !domImageHeight) {
        return;
      }
      let [canvasImageWidth, canvasImageHeight] = getImageDimensions(domImageHeight, domImageWidth, stageWidth, stageHeight);
      canvasImageHeight = canvasImageHeight * scale;
      canvasImageWidth = canvasImageWidth * scale;
      canvasContext.clearRect(0, 0, stageWidth, stageHeight);
      canvasContext.save();
      canvasContext.translate(stageWidth / 2, stageHeight / 2);
      canvasContext.rotate(rotation * Math.PI / 180);
      canvasContext.drawImage(imgRef.current, -canvasImageWidth / 2 + offsets.x, -canvasImageHeight / 2 + offsets.y, canvasImageWidth, canvasImageHeight);
      canvasContext.restore();
      try {
        enableImageSelection === true && onImageLoaded &&
        // @ts-expect-error todo(ts-migration) TS2339 Property 'toDataURL' does not exist on type 'never'.
        onImageLoaded(canvas.toDataURL('image/jpeg'));
      } catch (e) {
        console.error(`Unable to read image data.`);
        // TODO(prateekbh@): Log a warning about CORS issue
      }
    }
  }, [imageSize, canvasSize, onImageLoaded, enableImageSelection, rotation, scale, offsets]);
  const captureMouseUp = (e: MouseEvent<HTMLCanvasElement>) => {
    if (!onReferenceMouseUp) {
      return;
    }
    onReferenceMouseUp(tranformMouseEventForReference(e));
  };
  const captureMouseDown = (e: MouseEvent<HTMLCanvasElement>) => {
    if (!onReferenceMouseDown) {
      return;
    }
    onReferenceMouseDown(tranformMouseEventForReference(e));
  };
  const captureMouseMove = (e: MouseEvent<HTMLCanvasElement>) => {
    if (!onReferenceMouseMove) {
      return;
    }
    const event = tranformMouseEventForReference(e);
    const {
      referenceMovementX,
      referenceMovementY
    } = event;
    switch (rotation) {
      case 90:
      case 270:
        event.referenceMovementX = rotation === 270 ? -1 * referenceMovementY : referenceMovementY;
        event.referenceMovementY = rotation === 90 ? -1 * referenceMovementX : referenceMovementX;
        break;
      case 180:
        event.referenceMovementX = -1 * referenceMovementX;
        event.referenceMovementY = -1 * referenceMovementY;
        break;
    }
    onReferenceMouseMove(event);
  };
  const captureWheelMove = ({
    deltaY
  }) => {
    onMouseWheelMove && onMouseWheelMove(deltaY);
  };
  return (
    // @ts-expect-error missing null check
    <Container ref={containerRef}>
      {!imageLoaded && <StyledSpinner data-testid="spinner" />}
      <div>
        {isError ? <ErrorBlock data-testid={'error-message'}>Failed to load image</ErrorBlock> : <canvas
        // @ts-expect-error missing null check
        ref={canvasRef} data-testid={'canvas'} width={canvasSize.w} height={canvasSize.h} onMouseUp={captureMouseUp} onMouseDown={captureMouseDown} onMouseMove={captureMouseMove} onWheel={captureWheelMove} />}
      </div>
      <HiddenImage src={src} onLoad={() => {
        setImageLoaded(true);
      }} data-testid={'canvas-image'} onError={() => {
        setImageLoaded(true);
        setIsError(true);
      }} crossOrigin={enableImageSelection ? 'anonymous' : undefined}
      // @ts-expect-error missing null check
      ref={imgRef} />
      {imageLoaded && !hideAspectRatio && <ImageDetails title="Aspect Ratio">{`AR: ${aspectRatio} (${imageSize.w}x${imageSize.h})`}</ImageDetails>}
    </Container>
  );
}